import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Person, PersonRelationshipSummary, Photo, LifeEvent } from '../types';
import { PersonService } from './personService';
import { RelationshipService } from './relationshipService';
import TimelineService from './timelineService';
import PhotoService from './photoService';

interface ProfileData {
  person: Person;
  relationships: PersonRelationshipSummary;
  photos: Photo[];
  timeline: LifeEvent[];
  completeness: number;
}

interface ProfileStats {
  relationshipCount: number;
  photoCount: number;
  timelineEventCount: number;
  lastUpdated: Date;
}

export class ProfileService {
  /**
   * Get comprehensive profile data for a person
   */
  static async getProfileData(personId: string): Promise<ProfileData> {
    try {
      // Load person data first
      const person = await PersonService.getPersonById(personId);
      
      if (!person) {
        throw new Error('Person not found');
      }

      // Try to load relationships, but handle gracefully if there are database issues
      let relationships: PersonRelationshipSummary;
      try {
        relationships = await RelationshipService.getPersonRelationships(personId);
      } catch (error) {
        console.warn('Could not load relationships, using empty relationships:', error);
        // Provide empty relationships structure if database query fails
        relationships = {
          personId,
          parents: [],
          children: [],
          spouses: [],
          siblings: [],
        };
      }

      // Load actual photos using PhotoService
      let photos: Photo[] = [];
      try {
        photos = await PhotoService.getPhotosForPerson(personId);
      } catch (error) {
        console.warn('Could not load photos, using empty photos array:', error);
        photos = [];
      }
      
      // Load actual timeline data using TimelineService
      let timeline: LifeEvent[] = [];
      try {
        const extendedEvents = await TimelineService.getPersonTimeline(personId);
        // Convert ExtendedLifeEvent to LifeEvent
        timeline = extendedEvents.map(event => ({
          ...event,
          createdAt: event.createdAt instanceof Date ? event.createdAt : event.createdAt.toDate()
        }));
      } catch (error) {
        console.warn('Could not load timeline events, generating basic events from person data:', error);
        // Fallback to generating basic events from person data
        timeline = this.generateTimelineFromPerson(person);
      }

      const completeness = this.calculateProfileCompleteness(
        person,
        relationships,
        photos,
        timeline
      );

      return {
        person,
        relationships,
        photos,
        timeline,
        completeness,
      };
    } catch (error) {
      // Handle specific cases where person doesn't exist due to database issues
      if (error instanceof Error && error.message === 'Person not found') {
        throw error; // Re-throw person not found errors
      }
      
      // For other database errors, provide more graceful error message
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('permission') ||
          errorMessage.includes('collection') ||
          errorMessage.includes('index')) {
        throw new Error(`Profile data is temporarily unavailable. Please try again later.`);
      }
      
      throw new Error(`Failed to load profile data: ${errorMessage}`);
    }
  }

  /**
   * Reload timeline data for a person (for refreshing after CRUD operations)
   */
  static async getPersonTimeline(personId: string): Promise<LifeEvent[]> {
    try {
      const extendedEvents = await TimelineService.getPersonTimeline(personId);
      // Convert ExtendedLifeEvent to LifeEvent
      return extendedEvents.map(event => ({
        ...event,
        createdAt: event.createdAt instanceof Date ? event.createdAt : event.createdAt.toDate()
      }));
    } catch (error) {
      console.warn('Could not load timeline events, using basic person data:', error);
      // Fallback to basic person data
      const person = await PersonService.getPersonById(personId);
      if (!person) {
        throw new Error('Person not found');
      }
      return this.generateTimelineFromPerson(person);
    }
  }

  /**
   * Update person information
   */
  static async updatePerson(personId: string, updates: Partial<Person>): Promise<Person> {
    try {
      const personRef = doc(db, 'persons', personId);
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp(),
      };

      // Remove undefined values
      Object.keys(updateData).forEach(key => {
        if (updateData[key as keyof typeof updateData] === undefined) {
          delete updateData[key as keyof typeof updateData];
        }
      });

      await updateDoc(personRef, updateData);
      
      // Return updated person
      const updatedPerson = await PersonService.getPersonById(personId);
      if (!updatedPerson) {
        throw new Error('Failed to retrieve updated person');
      }

      return updatedPerson;
    } catch (error) {
      // Handle specific Firebase errors gracefully
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('permission') ||
          errorMessage.includes('collection') ||
          errorMessage.includes('Missing or insufficient permissions')) {
        throw new Error(`You don't have permission to update this profile. Please check your access rights.`);
      }
      
      throw new Error(`Failed to update person: ${errorMessage}`);
    }
  }

  /**
   * Calculate profile completeness percentage
   */
  static calculateProfileCompleteness(
    person: Person,
    relationships: PersonRelationshipSummary,
    photos: Photo[],
    timeline: LifeEvent[]
  ): number {
    let score = 0;
    
    // Basic information (40% weight)
    if (person.firstName && person.lastName) score += 10;
    if (person.birthDate) score += 8;
    if (person.birthPlace?.city) score += 5;
    if (person.biography && person.biography.length > 50) score += 8;
    if (person.occupation) score += 4;
    if (person.education) score += 3;
    if (person.gender) score += 2;
    
    // Relationships (30% weight)
    if (relationships.parents.length > 0) score += 10;
    if (relationships.spouses.length > 0) score += 8;
    if (relationships.children.length > 0) score += 7;
    if (relationships.siblings.length > 0) score += 5;
    
    // Photos (20% weight)
    if (photos.length > 0) score += 8;
    if (photos.length >= 3) score += 6;
    if (photos.some(p => p.isApproved)) score += 4;
    if (photos.some(p => p.taggedPersons && p.taggedPersons.length > 1)) score += 2;
    
    // Timeline events (10% weight)
    if (timeline.length > 2) score += 4;
    if (timeline.length >= 5) score += 3;
    if (timeline.some(e => e.type === 'education')) score += 2;
    if (timeline.some(e => e.type === 'employment')) score += 1;
    
    return Math.min(100, score);
  }

  /**
   * Get profile statistics
   */
  static async getProfileStats(personId: string): Promise<ProfileStats> {
    try {
      const profileData = await this.getProfileData(personId);
      
      return {
        relationshipCount: profileData.relationships.spouses.length +
                          profileData.relationships.children.length +
                          profileData.relationships.parents.length +
                          profileData.relationships.siblings.length,
        photoCount: profileData.photos.length,
        timelineEventCount: profileData.timeline.length,
        lastUpdated: profileData.person.updatedAt || profileData.person.createdAt,
      };
    } catch (error) {
      // Handle specific cases gracefully
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('permission') ||
          errorMessage.includes('collection') ||
          errorMessage.includes('temporarily unavailable')) {
        // Return basic stats structure if we can't load full profile data
        return {
          relationshipCount: 0,
          photoCount: 0,
          timelineEventCount: 0,
          lastUpdated: new Date(),
        };
      }
      
      throw new Error(`Failed to get profile stats: ${errorMessage}`);
    }
  }

  /**
   * Generate basic timeline from person data
   */
  private static generateTimelineFromPerson(person: Person): LifeEvent[] {
    const timeline: LifeEvent[] = [];

    // Birth event
    if (person.birthDate) {
      timeline.push({
        id: `birth-${person.id}`,
        personId: person.id,
        type: 'birth',
        date: person.birthDate,
        location: person.birthPlace,
        description: `Born in ${person.birthPlace?.city || 'unknown location'}`,
        createdBy: 'system',
        createdAt: new Date(),
      });
    }

    // Death event
    if (person.deathDate && person.isDeceased) {
      timeline.push({
        id: `death-${person.id}`,
        personId: person.id,
        type: 'death',
        date: person.deathDate,
        location: person.deathPlace,
        description: `Died in ${person.deathPlace?.city || 'unknown location'}`,
        createdBy: 'system',
        createdAt: new Date(),
      });
    }

    return timeline.sort((a, b) => {
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    });
  }

  /**
   * Export profile data
   */
  static async exportProfile(personId: string, format: 'json' = 'json'): Promise<any> {
    try {
      const profileData = await this.getProfileData(personId);
      
      return {
        exportDate: new Date().toISOString(),
        profile: profileData,
      };
    } catch (error) {
      // Handle specific cases gracefully
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      if (errorMessage.includes('permission') ||
          errorMessage.includes('collection') ||
          errorMessage.includes('temporarily unavailable')) {
        throw new Error(`Profile export is temporarily unavailable. Please try again later.`);
      }
      
      throw new Error(`Failed to export profile: ${errorMessage}`);
    }
  }
}